import { bool, object } from "yup";
import { dateMax, stringMax } from "./utils";
import { ALL_DIAGNOSTIC_TAGS } from "../utils/constants/documents";

export class FileStatus {
  static valide = new FileStatus(1);
  static aValider = new FileStatus(2);

  constructor(status) {
    this.status = status;
  }
}

export const documentSchema = object({
  tag: stringMax(250).required(" "),
  shared: bool().notRequired(),
  filename: stringMax(250).notRequired(),
});

export const documentSchemaWithValidity = documentSchema.concat(
  object({
    valid_until: dateMax().required(" "),
  }),
);

/** Diagnostics valables sans limite quand le risque est absent (plomb, amiante). */
export const documentSchemaWithOptionalValidity = documentSchema.concat(
  object({
    no_validity: bool().notRequired(),
    valid_until: dateMax().when("no_validity", {
      is: true,
      then: (schema) =>
        schema
          .notRequired()
          .nullable()
          .transform((value, original) => (original === "" ? null : value)),
      otherwise: (schema) => schema.required(" "),
    }),
  }),
);

export const documentSchemaWithDateSignature = documentSchema.concat(
  object({
    date_signature: dateMax().required(" "),
  }),
);
export const documentSchemaQuittance = documentSchema.concat(
  object({
    date_quittance: dateMax().required(" "),
  }),
);

export const documentSchemaWithYear = documentSchema.concat(
  object({
    year: stringMax(4).required("L'année est obligatoire"),
  }),
);

// Un seul PDF regroupe souvent plusieurs diagnostics : le document garde le tag
// choisi et liste les autres dans `meta.diagnostics`.
const DIAGNOSTIC_GROUP_FIELD = {
  type: "diagnostics",
  name: "diagnostics",
  label: "Ce document regroupe aussi",
};

export const DOCUMENT_LIST = [
  {
    nom: "Bail",
    tag: "bail",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    requiredTenant: true,
    fields: [
      { type: "date", name: "date_signature", label: "Date de signature" },
    ],
    needValidation: true,
    validationSchema: documentSchemaWithDateSignature,
  },
  {
    nom: "Etat des lieux",
    tag: "edl",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    requiredTenant: true,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Avis d'échéance",
    tag: "avis_echeance",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Assurance",
    tag: "assurance_habitation",
    vueBailleur: true,
    addLocataire: true,
    addProperty: false,
    vueLocataire: true,
    needValidation: true,
    requiredTenant: true,
    validityHint:
      "L'attestation d'assurance habitation est renouvelée chaque année par l'assureur.",
    fields: [
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Entretien chauffage",
    tag: "entretien_chauffage",
    vueBailleur: true,
    addLocataire: true,
    addProperty: false,
    vueLocataire: true,
    needValidation: true,
    requiredTenant: true,
    validityHint: "L'entretien de la chaudière doit être réalisé chaque année.",
    fields: [
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Justificatif domicile",
    tag: "domicile",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Justificatif professionnel",
    tag: "pro",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Justificatif identité",
    tag: "identite",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [
      { type: "date", name: "date_signature", label: "Date de signature" },
    ],
    needValidation: true,
    validationSchema: documentSchemaWithDateSignature,
  },
  {
    nom: "Courrier",
    tag: "courrier",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Congé",
    tag: "conge",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Avenant",
    tag: "avenant",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Visale",
    tag: "visale",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "GLI",
    tag: "gli",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Quittances / Factures",
    tag: "quittance",
    groupTags: ["quittance", "facture_loyer", "avoir"],
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [
      {
        type: "date",
        name: "date_quittance",
        label: "Periode de la quittance",
      },
    ],
    needValidation: true,
    validationSchema: documentSchemaQuittance,
  },
  {
    nom: "Recu",
    tag: "recu",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    validationSchema: documentSchema,
  },
  {
    nom: "Caution",
    tag: "caution",
    vueBailleur: true,
    vueLocataire: true,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Décompte",
    tag: "decompte",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: true,
    addProperty: false,
    fields: [],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Taxe foncière",
    tag: "taxe_fonciere",
    vueProperty: true,
    fields: [
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Taxe d'ordure ménagère",
    tag: "taxe_om",
    vueProperty: true,
    fields: [
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Diagnostic DPE",
    tag: "DPE",
    vueProperty: true,
    mandatoryProperty: true,
    validityHint:
      "Un DPE est valable 10 ans. Les DPE réalisés avant le 1er juillet 2021 ne sont plus valables.",
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Mesurage (loi Boutin)",
    tag: "mesurage",
    vueProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "Diagnostic électrique",
    tag: "elec",
    vueProperty: true,
    validityHint:
      "Le diagnostic électrique est valable 6 ans pour la location.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "État des risques et pollutions (ancien)",
    tag: "pollution",
    vueProperty: false,
    validityHint: "L'état des risques et pollutions est valable 6 mois.",
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Diagnostic plomb",
    tag: "plomb",
    vueProperty: true,
    validityHint: "Valable 6 ans en cas de présence de plomb.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      {
        type: "checkbox",
        name: "no_validity",
        label: "Absence de plomb constatée : validité illimitée",
      },
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithOptionalValidity,
  },
  {
    nom: "Diagnostic amiante",
    tag: "amiante",
    vueProperty: true,
    validityHint:
      "En cas de présence d'amiante, un contrôle périodique est dû sous 3 ans. Les diagnostics réalisés avant 2013 doivent être refaits.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      {
        type: "checkbox",
        name: "no_validity",
        label: "Absence d'amiante constatée : validité illimitée",
      },
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithOptionalValidity,
  },
  {
    nom: "Diagnostic gaz",
    tag: "gaz",
    vueProperty: true,
    validityHint: "Le diagnostic gaz est valable 6 ans pour la location.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "Diagnostic termites",
    tag: "termite",
    vueProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "État des risques et pollutions (ERP)",
    tag: "risque_naturel",
    vueProperty: true,
    validityHint:
      "L'état des risques et pollutions est valable 6 mois. Il se génère gratuitement sur errial.georisques.gouv.fr.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      { type: "date", name: "valid_until", label: "Date de fin de validité" },
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchemaWithValidity,
  },
  {
    nom: "État des nuisances sonores aériennes",
    tag: "bruit",
    vueProperty: true,
    validityHint:
      "Attendu pour un logement situé en zone de bruit d'un plan d'exposition au bruit (PEB) d'un aérodrome ; à établir par le bailleur pour chaque bail.",
    mandatoryProperty: true,
    fields: [
      DIAGNOSTIC_GROUP_FIELD,
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "Annexe environnementale",
    tag: "annexe_environnementale",
    vueProperty: true,
    validityHint:
      "Obligatoire pour un bail commercial portant sur plus de 2 000 m² de bureaux ou de commerces.",
    mandatoryProperty: true,
    fields: [
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "Réglement de copropriété",
    tag: "copro",
    vueProperty: true,
    fields: [
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "RIB",
    tag: "rib",
    vueProperty: true,
    requiredProperty: true,
    fields: [
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
      },
    ],
    validationSchema: documentSchema,
  },
  {
    nom: "Dernière liasse",
    tag: "liasse",
    vueProperty: true,
    requiredFiscalite: true,
    fields: [{ type: "year", name: "year", label: "Année du document" }],
    validationSchema: documentSchemaWithYear,
  },
  {
    nom: "Tableau d'amortissement",
    tag: "tableau_amortissement",
    vueProperty: true,
    requiredFiscalite: true,
    fields: [{ type: "year", name: "year", label: "Année du document" }],
    validationSchema: documentSchemaWithYear,
  },
  {
    nom: "Tableau d'immobilisation",
    tag: "tableau_immobilisation",
    vueProperty: true,
    requiredFiscalite: true,
    fields: [{ type: "year", name: "year", label: "Année du document" }],
    validationSchema: documentSchemaWithYear,
  },
  {
    nom: "Autre document",
    tag: "autre",
    vueBailleur: true,
    vueLocataire: true,
    addLocataire: true,
    addProperty: true,
    vueProperty: true,
    fields: [
      {
        type: "checkbox",
        name: "shared",
        label: "Partagé avec le locataire",
        checked: true,
        vueTenant: false,
      },
    ],
    needValidation: true,
    validationSchema: documentSchema,
  },
  {
    nom: "Charte de colocation",
    tag: "charte_colocation",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: false,
    addProperty: false,
    vueProperty: true,
    fields: [],
    needValidation: false,
    validationSchema: documentSchema,
  },
  {
    nom: "CRG",
    tag: "crg",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: false,
    addProperty: false,
    fields: [],
    needValidation: false,
    validationSchema: documentSchema,
  },
  {
    nom: "Facture d'honoraires",
    tag: "facture_honoraires",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: false,
    addProperty: false,
    fields: [],
    needValidation: false,
    validationSchema: documentSchema,
  },
  {
    nom: "KBIS",
    tag: "kbis",
    vueBailleur: true,
    vueLocataire: false,
    addLocataire: false,
    addProperty: false,
    fields: [],
    needValidation: false,
    validationSchema: documentSchema,
  },
];

export const isDiagnosticTag = (tag) => ALL_DIAGNOSTIC_TAGS.includes(tag);
